(() => {
  const hero = document.querySelector('[data-encore-hero]');
  if (!hero) return;
  if (hero.dataset.encoreHeroInitialized === 'true') return;
  hero.dataset.encoreHeroInitialized = 'true';

  const desktopQuery = window.matchMedia('(min-width: 990px)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const narrative = hero.querySelector('[data-encore-hero-narrative]');
  const heroContent = hero.querySelector('.encore-cinematic-hero__content');
  const heroCard = hero.querySelector('.encore-cinematic-hero__inner');
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  let ticking = false;
  let heroTop = 0;
  let heroScrollDistance = 1;
  let previousScrollY = window.scrollY;

  const clamp = (value, min = 0, max = 1) => Math.min(Math.max(value, min), max);
  const phase = (progress, start, end) => clamp((progress - start) / (end - start));
  const mix = (from, to, amount) => from + (to - from) * amount;
  const lerp = (from, to, amount) => from + (to - from) * amount;
  const easeOutCubic = (value) => 1 - Math.pow(1 - value, 3);
  const easeInOutCubic = (value) => value < 0.5
    ? 4 * value * value * value
    : 1 - Math.pow(-2 * value + 2, 3) / 2;
  const smoothstep = (edge0, edge1, value) => {
    const amount = clamp((value - edge0) / (edge1 - edge0), 0, 1);
    return amount * amount * (3 - 2 * amount);
  };

  const measureHero = () => {
    const rect = hero.getBoundingClientRect();
    heroTop = rect.top + window.scrollY;
    heroScrollDistance = Math.max(hero.offsetHeight - window.innerHeight, 1);
  };

  const resetNarrative = () => {
    if (!narrative) return;
    narrative.style.removeProperty('--hero-marquee-y');
    narrative.style.removeProperty('--hero-marquee-opacity');
    narrative.style.removeProperty('--hero-marquee-visibility');
    narrative.style.removeProperty('--hero-story-y');
    narrative.style.removeProperty('--hero-story-opacity');
    hero.style.removeProperty('--hero-dark-overlay-opacity');
    heroContent?.style.removeProperty('--hero-card-opacity');
    heroContent?.style.removeProperty('--hero-card-y');
    heroCard?.style.removeProperty('pointer-events');
  };

  const updateHomepageHeaderState = (progress, scrollY) => {
    const root = document.documentElement;

    if (!desktopQuery.matches) {
      root.classList.remove('encore-home-header-visible', 'encore-home-header-after-hero');
      previousScrollY = scrollY;
      return;
    }

    const heroHeaderActive = progress < 0.88;
    const scrollingUp = scrollY < previousScrollY;

    root.classList.toggle('encore-home-header-visible', heroHeaderActive || scrollingUp);
    root.classList.toggle('encore-home-header-after-hero', !heroHeaderActive);
    previousScrollY = scrollY;
  };

  const updateNarrative = () => {
    ticking = false;

    const scrollY = window.scrollY;
    const progress = clamp((scrollY - heroTop) / heroScrollDistance);
    updateHomepageHeaderState(progress, scrollY);

    if (!narrative || mobileQuery.matches || !desktopQuery.matches || reducedMotionQuery.matches) {
      resetNarrative();
      return;
    }

    const phase2 = phase(progress, 0.12, 0.42);
    const phase4 = phase(progress, 0.62, 0.82);

    const marqueeY = `${lerp(0, -72, easeInOutCubic(progress)).toFixed(2)}vh`;
    let marqueeOpacity = 1 - smoothstep(0.58, 0.92, progress);
    const marqueeVisible = progress < 0.94;
    let cardOpacity = 1;
    let cardY = 0;
    let overlayOpacity = 0;
    let storyOpacity = smoothstep(0.78, 0.96, progress);
    let storyY = 40;

    if (progress > 0.12) {
      const motion = easeOutCubic(phase2);
      cardOpacity = mix(1, 0, phase2);
      cardY = mix(0, -36, motion);
    }

    if (progress > 0.42) {
      cardOpacity = 0;
      cardY = -36;
      overlayOpacity = mix(0, 0.45, phase(progress, 0.42, 0.62));
    }

    if (progress > 0.62) {
      const motion = easeOutCubic(phase4);
      overlayOpacity = mix(0.45, 0.68, phase4);
      storyY = mix(40, 0, motion);
    }

    if (progress >= 0.94) {
      marqueeOpacity = 0;
    }

    narrative.style.setProperty('--hero-marquee-y', marqueeY);
    narrative.style.setProperty('--hero-marquee-opacity', marqueeOpacity.toFixed(3));
    narrative.style.setProperty('--hero-marquee-visibility', marqueeVisible ? 'visible' : 'hidden');
    hero.style.setProperty('--hero-dark-overlay-opacity', overlayOpacity.toFixed(3));
    narrative.style.setProperty('--hero-story-y', `${storyY.toFixed(2)}px`);
    narrative.style.setProperty('--hero-story-opacity', storyOpacity.toFixed(3));
    heroContent?.style.setProperty('--hero-card-opacity', cardOpacity.toFixed(3));
    heroContent?.style.setProperty('--hero-card-y', `${cardY.toFixed(2)}px`);

    if (heroCard) {
      heroCard.style.pointerEvents = cardOpacity < 0.08 ? 'none' : '';
    }
  };

  const requestNarrativeUpdate = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(updateNarrative);
  };

  const refreshNarrative = () => {
    if (mobileQuery.matches) {
      resetNarrative();
      return;
    }

    measureHero();
    requestNarrativeUpdate();
  };

  window.addEventListener('scroll', requestNarrativeUpdate, { passive: true });
  window.addEventListener('resize', refreshNarrative);
  window.addEventListener('load', refreshNarrative, { once: true });
  desktopQuery.addEventListener?.('change', refreshNarrative);
  mobileQuery.addEventListener?.('change', refreshNarrative);
  reducedMotionQuery.addEventListener?.('change', refreshNarrative);
  refreshNarrative();

  const closeHotspots = (except = null) => {
    hero.querySelectorAll('.encore-hero-hotspot.is-active').forEach((hotspot) => {
      if (hotspot === except) return;
      hotspot.classList.remove('is-active');
      hotspot.querySelector('[data-encore-hotspot]')?.setAttribute('aria-expanded', 'false');
    });
  };

  hero.querySelectorAll('[data-encore-hotspot]').forEach((button) => {
    button.addEventListener('click', (event) => {
      if (desktopQuery.matches) return;
      event.stopPropagation();
      const hotspot = button.closest('.encore-hero-hotspot');
      const isActive = !hotspot.classList.contains('is-active');
      closeHotspots(hotspot);
      hotspot.classList.toggle('is-active', isActive);
      button.setAttribute('aria-expanded', String(isActive));
    });
  });

  hero.querySelectorAll('.encore-hero-hotspot__card').forEach((card) => {
    card.addEventListener('click', (event) => event.stopPropagation());
  });

  document.addEventListener('click', () => closeHotspots());
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeHotspots();
  });
})();
